import { Table as MantineTable, Group, Text, useMantineTheme, Stack } from '@mantine/core';
import { type Table as TanstackTable, flexRender, type Row } from '@tanstack/react-table';

import classes from './MainTable.module.css';

import { ArrowDropdown, ArrowUp } from '../../icons';
import { type TableRows } from '../../types/tableTypes';

interface Props<T> {
  table: TanstackTable<T>
  onRowClick?: (row: Row<T>) => void
};

interface RowProps<T> {
  row: Row<T>
  onRowClick?: (row: Row<T>) => void
  className: string
}

const TableHead = <T extends TableRows>(props: Props<T>) => {
  const { table } = props;
  const { colors } = useMantineTheme();
  return (
    <MantineTable.Thead w={'100%'}>
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const color = (header.column.getIsSorted() === false)
              ? colors.darkPurple[9]
              : colors.lightPurple[5];
            return (
              <MantineTable.Th
                key={header.id}
                style={{
                  color,
                  h: 51,
                  w: `${header.getSize()}%`,
                  cursor: (header.column.getCanSort() ? 'pointer' : '')
                }}
              >
                {header.isPlaceholder
                  ? null
                  : (
                    <Group
                      gap={2}
                      {...{
                        onClick: header.column.getToggleSortingHandler()
                      }}
                    >
                      <Text fz='sm' c={color}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Text>{{
                        asc: <ArrowUp />,
                        desc: <ArrowDropdown />
                      }[header.column.getIsSorted() as string] ?? null}
                    </Group>
                    )}
              </MantineTable.Th>
            );
          })}
        </tr>
      ))}
    </MantineTable.Thead>
  );
};

const TableRow = <T extends TableRows>(props: RowProps<T>) => {
  const { row, onRowClick } = props;
  const cells = row.getVisibleCells();
  return (
    <>
      <MantineTable.Tr
        ta={'start'}
        onClick={() => { row.original.isClickable && onRowClick !== undefined && onRowClick(row); }}
        style={{
          cursor: row.original.isClickable ? 'pointer' : 'default',
          backgroundColor: !row.original.isClickable ? 'transparent' : undefined
        }}
      >
        {cells.map(cell => (
          <MantineTable.Td
            key={cell.id}
            style={{ width: `${cell.column.getSize()}%` }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </MantineTable.Td>
        ))}
      </MantineTable.Tr>
    </>
  );
};

const Table = <T extends TableRows>(props: Props<T>) => {
  const { table, onRowClick } = props;

  return (
    <Stack px={16} pb={16}>
      <MantineTable
        stickyHeader
        highlightOnHover={onRowClick !== undefined}
        classNames={{
          table: classes.table,
          th: classes.th,
          tbody: classes.tbody,
          tr: classes.tr,
          td: classes.td
        }}
      >
        <TableHead table={table} />
        <MantineTable.Tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.original.uid}
              onRowClick={onRowClick}
              row={row}
              className={row.original.isClickable ? classes.trClickable : classes.tr}
            />
          ))}
        </MantineTable.Tbody>
      </MantineTable>
    </Stack >
  );
};

export default Table;
